import { DotsThree } from '@phosphor-icons/react';
import React, {
  Children,
  ReactNode,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { cn } from '../../helpers/classBuilders';
import DropdownItem, { DropdownItemProps } from './DropdownItem';

interface DropdownProps {
  children: ReactNode;
  classBuilder: any;
  tabsHeight: number;
  forwardedRef: React.RefObject<HTMLLIElement | HTMLDivElement>;
  show: boolean;
  grouping: boolean;
}

interface GroupedItem {
  group?: string;
  children: ReactNode[];
}

const Dropdown: React.FC<DropdownProps> & { Item: typeof DropdownItem } = ({
  children,
  classBuilder,
  tabsHeight,
  forwardedRef,
  show,
  grouping,
}) => {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const c = classBuilder;
  const items = useMemo(() => {
    const filteredChildren = Children.toArray(children).filter(
      (child: any) =>
        React.isValidElement(child) && child.type === DropdownItem,
    );

    if (grouping) {
      return filteredChildren.reduce<GroupedItem[]>((acc, child) => {
        const reactElement = child as React.ReactElement<DropdownItemProps>;
        const { props } = reactElement;
        const { group } = props;

        if (group) {
          const groupIndex = acc.findIndex(
            ({ group: groupItem }) => groupItem === group,
          );
          if (groupIndex === -1) {
            acc.push({ group, children: [child] });
          } else {
            acc[groupIndex].children.push(child);
          }
        } else {
          acc.push({ children: [child] });
        }

        return acc;
      }, []);
    }

    return filteredChildren;
  }, [children]);

  const getStateClass = () => (open ? '__dropdown__container--open' : '');

  const toggleOnClick = () => setOpen(prev => !prev);

  const handleOnClick =
    (onClick: (eventKey: string) => void) => (eventKey: string) => {
      setOpen(false);
      onClick(eventKey);
    };

  // Revisit after moving to v2
  useLayoutEffect(() => {
    const listener = (event: MouseEvent) => {
      if (!forwardedRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('click', listener);
    }

    return () => {
      document.removeEventListener('click', listener);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (open) {
      menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }
  }, [open]);

  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    const menuItems = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    const currentIndex = menuItems.indexOf(
      document.activeElement as HTMLElement,
    );

    switch (e.key) {
      case 'Escape': {
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        break;
      }
      case 'ArrowDown': {
        e.preventDefault();
        menuItems[(currentIndex + 1) % menuItems.length]?.focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        menuItems[
          (currentIndex - 1 + menuItems.length) % menuItems.length
        ]?.focus();
        break;
      }
      case 'Tab': {
        setOpen(false);
        break;
      }
    }
  };

  if (!show) {
    return <></>;
  }

  return (
    <li
      className={cn(
        c('__tabs_item', '__dropdown'),
        'bg-gray-100 hover:bg-gray-200',
      )}
      role="presentation"
      ref={forwardedRef as React.RefObject<HTMLLIElement>}
      style={{ maxHeight: tabsHeight }}
    >
      <button
        ref={triggerRef}
        id={`${menuId}-trigger`}
        className={cn(
          c('__dropdown__toggle'),
          'focus-visible:focus-ring-inset',
        )}
        aria-label="More tabs"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={toggleOnClick}
      >
        <DotsThree size={24} aria-hidden="true" />
      </button>
      <ul
        id={menuId}
        role="menu"
        aria-labelledby={`${menuId}-trigger`}
        className={c('__dropdown__container', getStateClass())}
        style={{ top: tabsHeight }}
        ref={menuRef}
        onKeyDown={handleMenuKeyDown}
      >
        {grouping
          ? items.map(({ group, children }: any, index: any) => (
              <li key={`tab-menu-group-${group}-${index}`}>
                <ul
                  role="group"
                  aria-label={group}
                  className={c('__dropdown__group')}
                >
                  <li
                    role="presentation"
                    className={c('__dropdown__group__title')}
                  >
                    {group}
                  </li>
                  {children.map(({ props }) => (
                    <DropdownItem
                      key={`tab-menu-item-${props.eventKey}`}
                      {...props}
                      className={c('__dropdown__item')}
                      classBuilder={classBuilder}
                      onClick={handleOnClick(props.onClick)}
                    />
                  ))}
                </ul>
              </li>
            ))
          : items.map((value: any, index: number) => (
              <DropdownItem
                key={`tab-menu-item-${value.props.eventKey}`}
                {...value.props}
                className={c('__dropdown__item')}
                classBuilder={classBuilder}
                onClick={handleOnClick(value.props.onClick)}
              />
            ))}
      </ul>
    </li>
  );
};

Dropdown.Item = DropdownItem;

export default Dropdown;
